/**
 * OroLoader (#2675) — branded loading indicator: the three ascending brand-mark
 * dots. CSS-keyframes port of the mobile Reanimated loader
 * (`mobile/src/components/ui/OroLoader.tsx`); geometry and colors derive from the
 * canonical `OroMark` artwork (`ORO_MARK_DOTS`) so the loader animates the SAME
 * mark the app icon carries — fixed artwork palette, not theme tokens.
 *
 * Variants:
 *   - bounce (default) — staggered squash-and-stretch cascade of the brand mark.
 *     For full-screen / large loading states. (The web prototype's ground shadows
 *     were dropped, #2609: on the dark Nightfall bg they read as artifacts.)
 *     ALSO the sanctioned mark for COMPACT icon slots (<20px): the mark is an SVG
 *     over a square viewBox, so it scales faithfully (no aspect skew) — match
 *     `size` to the slot (`size={16}` for a ~16–18px hole). Prefer this over
 *     `inline` in tight slots — the inline row is a fixed ~43px and overflows
 *     them. Ratified #3206 (it is the
 *     AvatarRemoveButton pattern the #2690 spinner sweep settled on).
 *   - inline — three small "pace dots" in a FIXED ~43px row (ignores `size`). For
 *     buttons and in-context loads that HAVE room. On dark-scrim overlays
 *     (bg-black/50 image scrims) pass `color`/`text-white` so the dots stay
 *     visible over any image (ChatInputBar, ImageUpload — #3204/#3206). For
 *     <20px slots use `bounce size={16}` instead.
 *
 * Reduced motion: when the OS/app asks for less motion — or the caller passes
 * `animate={false}` — the loader degrades to the STATIC brand mark (bounce →
 * `<OroMark/>`; inline → full-opacity dots), i.e. the icon itself rather than a
 * frozen mid-bounce frame. Same behavior contract as mobile and #750.
 *
 * Keyframes live in `index.css` (`oro-splash-bounce`, `oro-pace`) — matching the
 * repo convention for animations.
 */
import { useState } from 'react';
import { getInitialReducedMotion } from '../utils/motionPreferences';
import { ORO_MARK_DOTS, ORO_MARK_VIEWBOX, OroMark } from './OroMark';

export type OroLoaderVariant = 'bounce' | 'inline';

export interface OroLoaderProps {
  /**
   * Stage size in px (square) for the bounce variant. Default 72; use `size={16}`
   * for compact <20px icon slots — bounce is the sanctioned compact mark (#3206).
   * Ignored for inline (its pace-dot row is a fixed ~43px).
   */
  size?: number;
  /** 'bounce' (splash cascade) | 'inline' (pace dots). Default 'bounce'. */
  variant?: OroLoaderVariant;
  /** Accessible label announced by screen readers. Default 'Loading'. */
  label?: string;
  /**
   * Single-color override for the INLINE dots — for contrast on tinted surfaces
   * (e.g. a gold primary button, where the gold brand dot would vanish). Ignored
   * by the bounce variant, whose colors are the artwork.
   */
  color?: string;
  /**
   * When `true` (default), play the cascade/pace animation — unless the user
   * prefers reduced motion. When `false`, render the static mark / dots
   * regardless of the motion preference. (The prop enables animation; the name
   * is not a `disableAnimation` inverse.)
   */
  animate?: boolean;
  /**
   * Render decorative (aria-hidden, no role) — for embedding inside a surface
   * that already owns a `role="status"` live region (e.g. LoadingPage), so the
   * loading state isn't announced twice.
   */
  decorative?: boolean;
  className?: string;
  'data-testid'?: string;
}

/** Diameter (px) of one inline pace dot. Mirrors mobile's PACE_DOT_SIZE. */
const PACE_DOT_SIZE = 9;
/** Gap (px) between inline pace dots. Mirrors mobile's PACE_GAP. */
const PACE_GAP = 8;

export function OroLoader({
  size = 72,
  variant = 'bounce',
  label = 'Loading',
  color,
  animate = true,
  decorative = false,
  className,
  'data-testid': testId,
}: OroLoaderProps) {
  // Read the app's reduced-motion preference once on mount (localStorage
  // override then the OS `prefers-reduced-motion` media query — the same source
  // the settings toggle writes, via getInitialReducedMotion). Either opt-out
  // renders the static mark / dots.
  //
  // Intentionally evaluated ONCE on mount (no matchMedia 'change' subscription):
  // this is an ephemeral loader that unmounts well before a user would toggle
  // the OS/app motion setting mid-render, matching the mobile port. The former
  // ProtectedRoute/LoadingPage spinners subscribed to live changes; that
  // reactivity is unnecessary here.
  const [prefersReducedMotion] = useState(getInitialReducedMotion);
  const shouldAnimate = animate && !prefersReducedMotion;

  // Accessibility: own the live region unless embedded in one (decorative).
  // `role="status"` takes its accessible name from `aria-label` (it is NOT a
  // name-from-content role), so the label must be on the container itself.
  const a11yProps = decorative
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'status', 'aria-live': 'polite', 'aria-label': label } as const);

  if (variant === 'inline') {
    const dotColors = color ? [color, color, color] : ORO_MARK_DOTS.map((d) => d.color);
    return (
      <span
        {...a11yProps}
        className={className}
        data-testid={testId}
        style={{ display: 'inline-flex', alignItems: 'center', gap: `${PACE_GAP}px` }}
      >
        {dotColors.map((dotColor, i) => (
          <span
            key={`pace-dot-${i}`}
            data-testid={`oro-loader-pace-${i}`}
            className={
              shouldAnimate
                ? `oro-loader-pace-dot${i > 0 ? ` oro-loader-pace-dot--${i}` : ''}`
                : undefined
            }
            style={{
              width: `${PACE_DOT_SIZE}px`,
              height: `${PACE_DOT_SIZE}px`,
              borderRadius: '9999px',
              backgroundColor: dotColor,
            }}
          />
        ))}
      </span>
    );
  }

  // Bounce variant.
  if (!shouldAnimate) {
    return (
      <span
        {...a11yProps}
        className={className}
        data-testid={testId}
        style={{ display: 'inline-flex' }}
      >
        <OroMark size={size} decorative />
      </span>
    );
  }

  return (
    <span
      {...a11yProps}
      className={className}
      data-testid={testId}
      style={{ display: 'inline-flex' }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${ORO_MARK_VIEWBOX} ${ORO_MARK_VIEWBOX}`} aria-hidden>
        {ORO_MARK_DOTS.map((dot, i) => (
          <circle
            key={dot.color}
            data-testid={`oro-loader-dot-${i}`}
            className={`oro-loader-bounce-dot${i > 0 ? ` oro-loader-bounce-dot--${i}` : ''}`}
            cx={dot.cx}
            cy={dot.cy}
            r={dot.r}
            fill={dot.color}
          />
        ))}
      </svg>
    </span>
  );
}

export default OroLoader;
